import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  Clock, Plus, X, Check, Calendar, AlertTriangle, ChevronDown, ChevronUp,
  Coffee, Sun, Moon, Trash2, Edit2, Copy
} from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DEFAULT_SCHEDULE = DAYS.map((_, i) => ({
  day_of_week: i,
  is_working: i < 5, // Mon-Fri default
  time_blocks: i < 5 ? [{ start_time: "09:00", end_time: "17:00" }] : [],
  breaks: i < 5 ? [{ start_time: "13:00", end_time: "14:00" }] : []
}));

export default function AvailabilityEditor({ practitionerId, practitionerName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [bufferBefore, setBufferBefore] = useState(0);
  const [bufferAfter, setBufferAfter] = useState(0);
  const [exceptions, setExceptions] = useState([]);
  const [showExceptions, setShowExceptions] = useState(false);
  const [newException, setNewException] = useState(null);

  // Load practitioner schedule and appointment types
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load schedule
        const { data: scheduleData } = await api.get(`/availability/schedule/${practitionerId}`);
        if (scheduleData.schedule?.schedule) {
          setSchedule(scheduleData.schedule.schedule);
          setSelectedTypeIds(scheduleData.schedule.appointment_type_ids || []);
          setBufferBefore(scheduleData.schedule.default_buffer_before || 0);
          setBufferAfter(scheduleData.schedule.default_buffer_after || 0);
        }

        // Load appointment types
        const { data: typesData } = await api.get("/availability/appointment-types");
        setAppointmentTypes(typesData.appointment_types || []);

        // Load upcoming exceptions
        const today = new Date().toISOString().split("T")[0];
        const futureDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        const { data: excData } = await api.get(`/availability/exceptions/${practitionerId}?start_date=${today}&end_date=${futureDate}`);
        setExceptions(excData.exceptions || []);
      } catch (err) {
        console.error("Failed to load availability:", err);
        toast.error("Failed to load availability settings");
      }
      setLoading(false);
    };
    loadData();
  }, [practitionerId]);

  // Save schedule
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/availability/schedule/${practitionerId}`, {
        practitioner_id: practitionerId,
        schedule,
        appointment_type_ids: selectedTypeIds,
        default_buffer_before: bufferBefore,
        default_buffer_after: bufferAfter
      });
      toast.success("Availability saved");
    } catch (err) {
      console.error("Failed to save:", err);
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  // Update day schedule
  const updateDay = (dayIndex, updates) => {
    setSchedule(prev => prev.map((d, i) => i === dayIndex ? { ...d, ...updates } : d));
  };

  // Add/remove time block
  const addTimeBlock = (dayIndex) => {
    const day = schedule[dayIndex];
    const lastBlock = day.time_blocks[day.time_blocks.length - 1];
    const newStart = lastBlock ? lastBlock.end_time : "09:00";
    const newEnd = lastBlock ? 
      `${String(parseInt(lastBlock.end_time.split(":")[0]) + 2).padStart(2, "0")}:00` : "12:00";
    updateDay(dayIndex, {
      time_blocks: [...day.time_blocks, { start_time: newStart, end_time: newEnd }]
    });
  };

  const removeTimeBlock = (dayIndex, blockIndex) => {
    const day = schedule[dayIndex];
    updateDay(dayIndex, {
      time_blocks: day.time_blocks.filter((_, i) => i !== blockIndex)
    });
  };

  const updateTimeBlock = (dayIndex, blockIndex, field, value) => {
    const day = schedule[dayIndex];
    const newBlocks = [...day.time_blocks];
    newBlocks[blockIndex] = { ...newBlocks[blockIndex], [field]: value };
    updateDay(dayIndex, { time_blocks: newBlocks });
  };

  // Add/remove break
  const addBreak = (dayIndex) => {
    const day = schedule[dayIndex];
    updateDay(dayIndex, {
      breaks: [...day.breaks, { start_time: "13:00", end_time: "14:00" }]
    });
  };

  const removeBreak = (dayIndex, breakIndex) => {
    const day = schedule[dayIndex];
    updateDay(dayIndex, {
      breaks: day.breaks.filter((_, i) => i !== breakIndex)
    });
  };

  const updateBreak = (dayIndex, breakIndex, field, value) => {
    const day = schedule[dayIndex];
    const newBreaks = [...day.breaks];
    newBreaks[breakIndex] = { ...newBreaks[breakIndex], [field]: value };
    updateDay(dayIndex, { breaks: newBreaks });
  };

  // Copy day schedule to other days
  const copyToOtherDays = (sourceDayIndex) => {
    const sourceDay = schedule[sourceDayIndex];
    setSchedule(prev => prev.map((d, i) => {
      if (i === sourceDayIndex) return d;
      return {
        ...d,
        time_blocks: [...sourceDay.time_blocks],
        breaks: [...sourceDay.breaks]
      };
    }));
    toast.success(`Copied ${DAYS[sourceDayIndex]}'s schedule to all weekdays`);
  };

  // Exception handlers
  const saveException = async () => {
    if (!newException) return;
    try {
      await api.post("/availability/exceptions", {
        practitioner_id: practitionerId,
        ...newException
      });
      toast.success("Exception saved");
      setNewException(null);
      // Reload exceptions
      const today = new Date().toISOString().split("T")[0];
      const futureDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
      const { data } = await api.get(`/availability/exceptions/${practitionerId}?start_date=${today}&end_date=${futureDate}`);
      setExceptions(data.exceptions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save exception");
    }
  };

  const deleteException = async (exceptionId) => {
    try {
      await api.delete(`/availability/exceptions/${exceptionId}`);
      setExceptions(prev => prev.filter(e => e.id !== exceptionId));
      toast.success("Exception deleted");
    } catch (err) {
      toast.error("Failed to delete exception");
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="w-6 h-6 border-2 border-[#205B5A] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-[#757675] mt-2">Loading availability...</p>
      </div>
    );
  }

  return (
    <div className="max-h-[85vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-[#E8E9E8] px-6 py-4 flex items-center justify-between z-10">
        <div>
          <h2 className="text-lg font-semibold text-[#1A1B1A]">Availability Settings</h2>
          <p className="text-sm text-[#757675]">{practitionerName}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-sm text-[#757675] hover:text-[#1A1B1A] px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm font-medium text-white bg-[#205B5A] hover:bg-[#174544] px-4 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5" />
                Save changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Weekly Schedule */}
        <div>
          <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#205B5A]" />
            Weekly schedule
          </h3>
          <div className="bg-white border border-[#E8E9E8] rounded-xl overflow-hidden">
            {schedule.map((day, dayIndex) => (
              <div key={dayIndex} className={`border-b border-[#E8E9E8] last:border-b-0 ${!day.is_working ? "bg-[#FAF9F6]" : ""}`}>
                <div className="px-4 py-3 flex items-center gap-4">
                  {/* Day toggle */}
                  <button
                    onClick={() => updateDay(dayIndex, { is_working: !day.is_working })}
                    className={`w-24 text-left text-sm font-medium transition-colors ${
                      day.is_working ? "text-[#1A1B1A]" : "text-[#757675] line-through"
                    }`}
                  >
                    {DAYS[dayIndex]}
                  </button>

                  {/* Working toggle */}
                  <button
                    onClick={() => updateDay(dayIndex, { is_working: !day.is_working })}
                    className={`w-10 h-5 rounded-full transition-colors flex items-center ${
                      day.is_working ? "bg-[#205B5A] justify-end" : "bg-[#D6D7D6] justify-start"
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full mx-0.5 shadow-sm" />
                  </button>

                  {day.is_working && (
                    <div className="flex-1 flex flex-wrap items-center gap-2">
                      {/* Time blocks */}
                      {day.time_blocks.map((block, blockIndex) => (
                        <div key={blockIndex} className="flex items-center gap-1.5 bg-[#205B5A]/5 px-2 py-1 rounded-lg">
                          <Sun className="w-3 h-3 text-[#205B5A]" />
                          <input
                            type="time"
                            value={block.start_time}
                            onChange={(e) => updateTimeBlock(dayIndex, blockIndex, "start_time", e.target.value)}
                            className="text-xs bg-transparent border-none focus:outline-none text-[#1A1B1A] w-16"
                          />
                          <span className="text-xs text-[#757675]">to</span>
                          <input
                            type="time"
                            value={block.end_time}
                            onChange={(e) => updateTimeBlock(dayIndex, blockIndex, "end_time", e.target.value)}
                            className="text-xs bg-transparent border-none focus:outline-none text-[#1A1B1A] w-16"
                          />
                          <button
                            onClick={() => removeTimeBlock(dayIndex, blockIndex)}
                            className="text-[#757675] hover:text-red-500 ml-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}

                      {/* Breaks */}
                      {day.breaks.map((brk, breakIndex) => (
                        <div key={`break-${breakIndex}`} className="flex items-center gap-1.5 bg-amber-50 px-2 py-1 rounded-lg">
                          <Coffee className="w-3 h-3 text-amber-600" />
                          <input
                            type="time"
                            value={brk.start_time}
                            onChange={(e) => updateBreak(dayIndex, breakIndex, "start_time", e.target.value)}
                            className="text-xs bg-transparent border-none focus:outline-none text-[#1A1B1A] w-16"
                          />
                          <span className="text-xs text-[#757675]">to</span>
                          <input
                            type="time"
                            value={brk.end_time}
                            onChange={(e) => updateBreak(dayIndex, breakIndex, "end_time", e.target.value)}
                            className="text-xs bg-transparent border-none focus:outline-none text-[#1A1B1A] w-16"
                          />
                          <button
                            onClick={() => removeBreak(dayIndex, breakIndex)}
                            className="text-[#757675] hover:text-red-500 ml-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}

                      {/* Add buttons */}
                      <button
                        onClick={() => addTimeBlock(dayIndex)}
                        className="text-[10px] text-[#205B5A] hover:text-[#174544] flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Hours
                      </button>
                      <button
                        onClick={() => addBreak(dayIndex)}
                        className="text-[10px] text-amber-600 hover:text-amber-700 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Break
                      </button>
                      {dayIndex === 0 && (
                        <button
                          onClick={() => copyToOtherDays(dayIndex)}
                          className="text-[10px] text-[#496275] hover:text-[#1A1B1A] flex items-center gap-1 ml-2"
                        >
                          <Copy className="w-3 h-3" /> Copy to all
                        </button>
                      )}
                    </div>
                  )}

                  {!day.is_working && (
                    <span className="text-xs text-[#757675]">Not working</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Buffers */}
        <div>
          <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#205B5A]" />
            Buffer time
          </h3>
          <div className="bg-white border border-[#E8E9E8] rounded-xl p-4">
            <p className="text-xs text-[#757675] mb-3">Add padding between appointments for admin tasks, preparation, or cleanup.</p>
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="text-[10px] font-medium text-[#757675] uppercase">Before appointments</label>
                <select
                  value={bufferBefore}
                  onChange={(e) => setBufferBefore(parseInt(e.target.value))}
                  className="mt-1 block w-32 text-sm border border-[#E8E9E8] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#205B5A]/20"
                >
                  <option value="0">None</option>
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-medium text-[#757675] uppercase">After appointments</label>
                <select
                  value={bufferAfter}
                  onChange={(e) => setBufferAfter(parseInt(e.target.value))}
                  className="mt-1 block w-32 text-sm border border-[#E8E9E8] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#205B5A]/20"
                >
                  <option value="0">None</option>
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Types */}
        <div>
          <h3 className="text-sm font-semibold text-[#1A1B1A] mb-3">Appointment types offered</h3>
          <div className="bg-white border border-[#E8E9E8] rounded-xl p-4">
            <p className="text-xs text-[#757675] mb-3">Select which appointment types this practitioner can perform.</p>
            <div className="flex flex-wrap gap-2">
              {appointmentTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => {
                    if (selectedTypeIds.includes(type.id)) {
                      setSelectedTypeIds(prev => prev.filter(id => id !== type.id));
                    } else {
                      setSelectedTypeIds(prev => [...prev, type.id]);
                    }
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    selectedTypeIds.includes(type.id)
                      ? "bg-[#205B5A] text-white border-[#205B5A]"
                      : "bg-white text-[#4A4B4A] border-[#E8E9E8] hover:border-[#205B5A]"
                  }`}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: type.color || "#205B5A" }}
                  />
                  {type.name} ({type.duration_minutes}min)
                </button>
              ))}
              {appointmentTypes.length === 0 && (
                <p className="text-xs text-[#757675]">No appointment types configured. Add them in Settings → Appointment Types.</p>
              )}
            </div>
          </div>
        </div>

        {/* Exceptions */}
        <div>
          <button
            onClick={() => setShowExceptions(!showExceptions)}
            className="text-sm font-semibold text-[#1A1B1A] mb-3 flex items-center gap-2 w-full"
          >
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Date exceptions
            <span className="text-xs text-[#757675] font-normal">({exceptions.length})</span>
            {showExceptions ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
          </button>

          {showExceptions && (
            <div className="bg-white border border-[#E8E9E8] rounded-xl p-4 space-y-3">
              <p className="text-xs text-[#757675]">Add holidays, sick days, or custom hours for specific dates.</p>

              {/* Existing exceptions */}
              {exceptions.map(exc => (
                <div key={exc.id} className="flex items-center justify-between bg-[#FAF9F6] rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-[#1A1B1A]">{exc.date}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      exc.exception_type === "holiday" ? "bg-blue-100 text-blue-700" :
                      exc.exception_type === "sick" ? "bg-red-100 text-red-700" :
                      exc.exception_type === "unavailable" ? "bg-gray-100 text-gray-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>
                      {exc.label || exc.exception_type}
                    </span>
                    {exc.time_blocks?.length > 0 && (
                      <span className="ml-2 text-xs text-[#757675]">
                        {exc.time_blocks.map(b => `${b.start_time}-${b.end_time}`).join(", ")}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => deleteException(exc.id)}
                    className="text-[#757675] hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {/* Add new exception */}
              {newException ? (
                <div className="border border-dashed border-[#D6D7D6] rounded-lg p-3 space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="date"
                      value={newException.date || ""}
                      onChange={(e) => setNewException({ ...newException, date: e.target.value })}
                      className="text-sm border border-[#E8E9E8] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#205B5A]/20"
                    />
                    <select
                      value={newException.exception_type || "unavailable"}
                      onChange={(e) => setNewException({ ...newException, exception_type: e.target.value })}
                      className="text-sm border border-[#E8E9E8] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#205B5A]/20"
                    >
                      <option value="holiday">Holiday</option>
                      <option value="sick">Sick day</option>
                      <option value="unavailable">Unavailable</option>
                      <option value="custom">Custom hours</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Label (optional)"
                      value={newException.label || ""}
                      onChange={(e) => setNewException({ ...newException, label: e.target.value })}
                      className="flex-1 text-sm border border-[#E8E9E8] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#205B5A]/20"
                    />
                  </div>
                  {newException.exception_type === "custom" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#757675]">Custom hours:</span>
                      <input
                        type="time"
                        value={newException.time_blocks?.[0]?.start_time || "09:00"}
                        onChange={(e) => setNewException({
                          ...newException,
                          time_blocks: [{ start_time: e.target.value, end_time: newException.time_blocks?.[0]?.end_time || "17:00" }]
                        })}
                        className="text-sm border border-[#E8E9E8] rounded-lg px-2 py-1"
                      />
                      <span className="text-xs">to</span>
                      <input
                        type="time"
                        value={newException.time_blocks?.[0]?.end_time || "17:00"}
                        onChange={(e) => setNewException({
                          ...newException,
                          time_blocks: [{ start_time: newException.time_blocks?.[0]?.start_time || "09:00", end_time: e.target.value }]
                        })}
                        className="text-sm border border-[#E8E9E8] rounded-lg px-2 py-1"
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={saveException}
                      className="text-xs font-medium text-white bg-[#205B5A] px-3 py-1.5 rounded-lg hover:bg-[#174544]"
                    >
                      Save exception
                    </button>
                    <button
                      onClick={() => setNewException(null)}
                      className="text-xs text-[#757675] hover:text-[#1A1B1A] px-3 py-1.5"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setNewException({ date: "", exception_type: "unavailable", label: "", time_blocks: [] })}
                  className="text-xs text-[#205B5A] hover:text-[#174544] flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Add exception
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
